/**
 * Machine estate controls — crafted pick + expand-to-edit + dispatch feedback.
 * Default collapsed (read); whole row is the hit target; Dispatch shows live ok/fail.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { html, M, stop } from "./ui.js";

/** Iris open — matches Skills / Jump dials (compositor-safe). */
const DIAL_IRIS = { type: "spring", stiffness: 460, damping: 30, mass: 0.62 };
const DIAL_OPT = { type: "tween", duration: 0.22, ease: [0.16, 1, 0.3, 1] };

/** Two-letter stamp from a unit label — field-tag glyph. */
export function stampGlyphOf(text) {
  return String(text || "?")
    .replace(/[^A-Za-z0-9\u4e00-\u9fff]/g, " ")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.slice(0, 1).toUpperCase())
    .join("") || "?";
}

/** Short serial from a device id (last token). */
export function serialTailOf(id) {
  const s = String(id || "").trim();
  if (!s) return "—";
  const parts = s.split(/[-_]/);
  const last = parts[parts.length - 1] || s;
  return last.slice(-8).toUpperCase();
}

/** Crafted listbox — portals to body; search; pointerdown pick (no ghost close). */
export function EstatePick({
  label = "Choose",
  value = "",
  options = [],
  placeholder = "Select…",
  onChange,
  disabled = false,
  compact = false,
  card = false,
  autoOpen = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [popStyle, setPopStyle] = useState(null);
  const root = useRef(null);
  const trig = useRef(null);
  const pop = useRef(null);
  const searchRef = useRef(null);
  const ignoreToggleUntil = useRef(0);
  const items = Array.isArray(options) ? options : [];
  const cur = String(value || "");
  const active = items.find((x) => String(x.id) === cur) || null;
  const show = active ? active.label : (cur || placeholder);
  const needle = String(q || "").trim().toLowerCase();
  const filtered = !needle
    ? items
    : items.filter((it) => {
        const blob = `${it.label || ""} ${it.say || ""} ${it.id || ""}`.toLowerCase();
        return blob.includes(needle);
      });
  const searchable = card || items.length >= 5;

  useEffect(() => {
    if (autoOpen && !disabled) setOpen(true);
  }, [autoOpen, disabled]);

  useEffect(() => {
    if (!open) {
      setPopStyle(null);
      setQ("");
      return;
    }
    const place = () => {
      const el = trig.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const width = Math.min(
        Math.max(r.width, card ? 300 : 260, 280),
        Math.max(280, window.innerWidth - 16),
      );
      let left = r.left;
      if (left + width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - width - 8);
      if (left < 8) left = 8;
      const spaceBelow = window.innerHeight - r.bottom - 12;
      const spaceAbove = r.top - 12;
      const preferUp = spaceBelow < 200 && spaceAbove > spaceBelow;
      const maxH = Math.min(card ? 380 : 320, preferUp ? spaceAbove : spaceBelow);
      setPopStyle({
        position: "fixed",
        top: preferUp ? undefined : r.bottom + 6,
        bottom: preferUp ? window.innerHeight - r.top + 6 : undefined,
        left,
        width,
        maxHeight: Math.max(160, maxH),
        zIndex: 7200,
      });
    };
    place();
    const t = setTimeout(() => {
      if (searchable && searchRef.current) {
        try { searchRef.current.focus(); } catch (_) { /* ignore */ }
      }
    }, 30);
    addEventListener("resize", place);
    addEventListener("scroll", place, true);
    return () => {
      clearTimeout(t);
      removeEventListener("resize", place);
      removeEventListener("scroll", place, true);
    };
  }, [open, items.length, card, searchable]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const t = e.target;
      if (root.current && root.current.contains(t)) return;
      if (pop.current && pop.current.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const t = setTimeout(() => {
      addEventListener("pointerdown", onDoc, true);
      addEventListener("keydown", onKey);
    }, 0);
    return () => {
      clearTimeout(t);
      removeEventListener("pointerdown", onDoc, true);
      removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (id) => {
    ignoreToggleUntil.current = Date.now() + 400;
    setOpen(false);
    onChange && onChange(String(id));
  };

  const onOptPointerDown = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    pick(id);
  };

  const panel = open
    ? createPortal(
        html`
          <div className="mh-pick__layer" data-card=${card ? "1" : "0"} data-portal="1">
            <button type="button" className="mh-pick__veil" aria-label="Close list"
              onClick=${() => setOpen(false)}/>
            <${M.div} className="mh-pick__pop" role="listbox" aria-label=${label}
              ref=${pop}
              data-fixed="1"
              data-portal="1"
              data-lux="1"
              data-motion="iris"
              data-card=${card ? "1" : "0"}
              style=${{
                ...(popStyle || { visibility: "hidden" }),
                transformOrigin: "top center",
              }}
              initial=${{ opacity: 0, y: -12, scaleY: 0.82, filter: "blur(6px)" }}
              animate=${{ opacity: 1, y: 0, scaleY: 1, filter: "blur(0px)" }}
              exit=${{ opacity: 0, y: -8, scaleY: 0.9, filter: "blur(4px)" }}
              transition=${DIAL_IRIS}
              onClick=${stop}
              onPointerDown=${(e) => e.stopPropagation()}>
              <span className="mh-filter__pop-glow" aria-hidden="true"/>
              <span className="mh-filter__pop-scan" aria-hidden="true"/>
              <header className="mh-pick__hd">
                <span>${label}</span>
                <small>${filtered.length}${needle ? ` / ${items.length}` : ""} choices</small>
              </header>
              ${searchable ? html`
                <div className="mh-pick__search">
                  <input ref=${searchRef} className="inp mh-pick__q" type="search"
                    placeholder=${"Find " + label.toLowerCase() + "…"}
                    value=${q}
                    aria-label=${"Find " + label}
                    onChange=${(e) => setQ(e.target.value)}
                    onKeyDown=${(e) => {
                      if (e.key === "Enter" && filtered.length === 1) {
                        e.preventDefault();
                        pick(filtered[0].id);
                      }
                    }}/>
                </div>` : null}
              <div className="mh-pick__list">
                ${!filtered.length
                  ? html`<p className="mh-pick__empty" role="status">No match — clear search or pick another.</p>`
                  : filtered.map((it, idx) => html`
                    <${M.button} key=${String(it.id) + "::" + String(it.label)} type="button" role="option"
                      className="mh-pick__opt"
                      aria-selected=${cur === String(it.id) ? "true" : "false"}
                      data-on=${cur === String(it.id) ? "1" : "0"}
                      initial=${{ opacity: 0, x: -10 }}
                      animate=${{ opacity: 1, x: 0 }}
                      transition=${{ ...DIAL_OPT, delay: Math.min(idx, 12) * 0.028 }}
                      onPointerDown=${(e) => onOptPointerDown(e, it.id)}>
                      <i className="mh-pick__mark" aria-hidden="true"/>
                      <span className="mh-pick__opt-copy">
                        <b>${it.label}</b>
                        ${it.say ? html`<small>${it.say}</small>` : null}
                      </span>
                      ${cur === String(it.id) ? html`<em className="mh-pick__on">on</em>` : null}
                    <//>`)}
              </div>
            <//>
          </div>`,
        document.body,
      )
    : null;

  return html`
    <div className="mh-pick" ref=${root}
      data-open=${open ? "1" : "0"}
      data-empty=${active || cur ? "0" : "1"}
      data-compact=${compact ? "1" : "0"}
      data-card=${card ? "1" : "0"}
      data-dial="1"
      data-lux="1"
      data-fixed-pop="1"
      data-portal-pop="1">
      <button type="button" className="mh-pick__trig" ref=${trig} disabled=${disabled}
        aria-haspopup="listbox" aria-expanded=${open ? "true" : "false"}
        aria-label=${label}
        onClick=${(e) => {
          stop(e);
          if (disabled) return;
          if (Date.now() < ignoreToggleUntil.current) return;
          setOpen((v) => !v);
        }}>
        <span className="mh-pick__copy">
          ${compact ? null : html`<small className="mh-pick__k">${label}</small>`}
          <b className="mh-pick__v">${show}</b>
        </span>
        <i className="mh-pick__chev" data-open=${open ? "1" : "0"} aria-hidden="true"/>
      </button>
      ${panel}
    </div>`;
}

/**
 * Collapsed read row → expand to edit → Save with live status.
 * status: { tone: "idle"|"send"|"go"|"bad"|"warn", say: string }
 */
export function EditRow({
  id,
  label,
  hint = "",
  display = "—",
  open = false,
  onToggle,
  status = null,
  busy = false,
  disabled = false,
  onDispatch,
  children,
}) {
  const st = status && typeof status === "object" ? status : null;
  const tone = st && st.tone ? st.tone : "idle";
  const say = st && st.say ? st.say : "";

  if (!open) {
    return html`
      <div className="mh-editrow" data-open="0" data-id=${id} data-tone=${tone} data-loc="1">
        <button type="button" className="mh-editrow__hit mh-editrow__hit--loc"
          aria-label=${"Edit " + label}
          aria-expanded="false"
          onClick=${() => onToggle && onToggle(id)}>
          <div className="mh-loc__main">
            <span className="mh-editrow__k">${label}</span>
            <b className="mh-editrow__v">${display || "—"}</b>
            <em className="mh-loc__edit" aria-hidden="true">▸ edit</em>
          </div>
        </button>
        ${say && tone !== "idle" ? html`
          <p className="mh-editrow__status" data-tone=${tone}>${say}</p>` : null}
      </div>`;
  }

  return html`
    <div className="mh-editrow" data-open="1" data-id=${id} data-tone=${tone} data-loc="1">
      <button type="button" className="mh-editrow__hit mh-editrow__hit--loc"
        aria-label=${"Collapse " + label}
        aria-expanded="true"
        onClick=${() => onToggle && onToggle("")}>
        <div className="mh-loc__main">
          <span className="mh-editrow__k">${label}</span>
          <b className="mh-editrow__v">${display || "—"}</b>
          <em className="mh-loc__edit" aria-hidden="true">▾ edit</em>
        </div>
      </button>
      ${hint ? html`<small className="mh-editrow__hint mh-editrow__hint--loc">${hint}</small>` : null}
      <div className="mh-editrow__body">${children}</div>
      <div className="mh-editrow__act">
        <button type="button" className="mh-editrow__dispatch"
          disabled=${busy || tone === "send" || disabled}
          onClick=${() => onDispatch && onDispatch(id)}>
          ${tone === "send" ? "Saving…" : "Save"}
        </button>
        <button type="button" className="mh-editrow__cancel"
          onClick=${() => onToggle && onToggle("")}>Cancel</button>
        ${say ? html`<p className="mh-editrow__status" data-tone=${tone}>${say}</p>` : html`
          <p className="mh-editrow__status" data-tone="idle">Save applies this field</p>`}
      </div>
    </div>`;
}

/** Readonly fact (never expands). stack=true → label above value. keyFact=true → soft highlight. */
export function FactRow({ label, value, hint = "", stack = false, keyFact = false }) {
  return html`
    <div className="mh-editrow" data-open="0" data-fact="1" data-stack=${stack ? "1" : "0"}
      data-key=${keyFact ? "1" : "0"}
      title=${hint || undefined}>
      <div className="mh-editrow__read mh-editrow__read--fact">
        <span className="mh-editrow__k">${label}</span>
        <b className="mh-editrow__v">${value || "—"}</b>
      </div>
    </div>`;
}
